import { App, Button, Carousel, Flex, Form, Spin } from 'antd'
import {
  LegacyRef,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react'
import NameStep from './steps/Name'
import { CarouselRef } from 'antd/es/carousel'
import EmailStep from './steps/Email'
import StepWrapper from './StepWrapper'
import PasswordStep from './steps/Password'
import IndustryStep from './steps/Industry'
import { useAuth } from '../../contexts/Auth'
import { RegisterData } from '../../models/AuthModel'
import axios from 'axios'
import { CSRF_COOKE_URL } from '../../helpers/urls'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import { useHypertune } from '../../generated/hypertune.react'
import i18n from '../../i18n/setupLocalization'
import LocalizationKeys from '../../i18n/LocalizationKeys'

const OnboardingRegistration = ({ children }: PropsWithChildren) => {
  const { register } = useAuth()
  const navigate = useNavigate()
  const hypertune = useHypertune()
  const { notification } = App.useApp()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [startHasBeenLogged, setStartHasBeenLogged] = useState<boolean>(false)

  const backableStepsIds = [1, 2, 3]

  const handleRegister = (values: RegisterData) => {
    hypertune.onboardingFillsIndustry({ args: { industryID: values.industry } })
    setIsLoading(true)

    axios.get(CSRF_COOKE_URL).then(() => {
      register(values)
        .then(() => {
          hypertune.onboardingRegisterSuccess()
          navigate('/onboarding/instagram')
        })
        .catch((err) => {
          hypertune.onboardingRegisterFails()
          notification.error({
            message: i18n.t(
              LocalizationKeys.Page.OnboardingRegistrationPage.Error
            ),
            description: i18n.t(
              LocalizationKeys.Page.OnboardingRegistrationPage.DescriptionError
            ),
            duration: 0,
          })
        })
        .finally(() => {
          setIsLoading(false)
        })
    })
  }

  const carouselRef: LegacyRef<CarouselRef> = useRef(null)
  const prevSlide = () => {
    if (!carouselRef.current) {
      return
    }
    carouselRef.current.prev()
    setCurrentStep(currentStep - (currentStep != 0 ? 1 : 0))
  }
  const nextSlide = () => {
    if (!carouselRef.current) {
      return
    }
    setCurrentStep(currentStep + 1)
    carouselRef.current.next()
  }

  useEffect(() => {
    if (!hypertune.isReady() || startHasBeenLogged) {
      return
    }

    hypertune.onboardingStarts()
    setStartHasBeenLogged(true)
  }, [hypertune])

  return (
    <Flex vertical>
      {isLoading && (
        <Flex
          align="center"
          justify="center"
          style={{
            position: 'absolute',
            background: '#2222224a',
            width: '100%',
            height: '100%',
            zIndex: 100,
          }}
        >
          <Spin />
        </Flex>
      )}

      <Flex
        style={{
          width: '100%',
          height: 64,
          padding: 20,
        }}
        align="center"
        justify="space-between"
      >
        {currentStep == 0 && (
          <img src="/assets/images/logos/regular/purple.png" height={45} />
        )}
        {backableStepsIds.includes(currentStep) && (
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => prevSlide()}
          >
            {i18n.t(LocalizationKeys.Page.OnboardingRegistrationPage.Back)}
          </Button>
        )}

        <Link to={'/auth/login'} style={{ marginLeft: 'auto' }}>
          <Button type={'primary'}>
            {i18n.t(LocalizationKeys.Page.Login.Submit)}
          </Button>
        </Link>
      </Flex>
      <Form layout="vertical" onFinish={handleRegister}>
        <Carousel
          ref={carouselRef}
          infinite={false}
          dotPosition="left"
          dots={false}
          style={{ height: 'calc(100vh - 128px)' }}
        >
          <StepWrapper>
            <NameStep
              onSuccess={() => {
                hypertune.onboardingFillsName()
                nextSlide()
              }}
            />
          </StepWrapper>

          <StepWrapper>
            <EmailStep
              onSuccess={() => {
                hypertune.onboardingFillsEmail()
                nextSlide()
              }}
            />
          </StepWrapper>

          <StepWrapper>
            <PasswordStep
              onSuccess={() => {
                hypertune.onboardingFillsPassword()
                nextSlide()
              }}
            />
          </StepWrapper>

          <StepWrapper>
            <IndustryStep />
          </StepWrapper>
        </Carousel>
      </Form>
    </Flex>
  )
}

export default OnboardingRegistration
